
// src/telemetry/TelemetryWrapper.jsx
import React from "react";

// Real telemetry provider (creates session and tracks events)
import { TelemetryProvider } from "./TelemetryProvider";

// No-op provider (safe fallback when telemetry is disabled)
import { NoopTelemetryProvider } from "./NoopTelemetryProvider";

// Your Redux-based token provider util
import { getDynamicToken } from "../utils/authUtils";

/**
 * TelemetryWrapper
 * Conditionally enables telemetry when `claimNumber` exists in the URL query.
 *
 * Usage:
 *   <TelemetryWrapper>
 *     <App />
 *   </TelemetryWrapper>
 *
 * Behavior:
 *   - If URL contains ?claimNumber=CLAIM-123 → TelemetryProvider wraps children
 *   - Otherwise → NoopTelemetryProvider wraps children (track() is a no-op)
 */
export default function TelemetryWrapper({ children }) {
  // Read claimNumber from URL query
  const query = window.location.search || "";
  const claimNumber = new URLSearchParams(query).get("claimNumber");

  // Base URL for telemetry API:
  // - Main app proxy:  http://<main-host>/telemetry
  // - Direct telemetry service: http://localhost:4000
  const baseUrl =
    import.meta.env.VITE_API_URL+"/telemetry";

  // Token provider using Redux (returns Promise<string | undefined>)
  const tokenProvider = async () => {
    try {
      const token = await getDynamicToken();
      return token || undefined; // undefined → no Authorization header
    } catch (e) {
      console.error("tokenProvider error:", e);
      return undefined;
    }
  };

  if (claimNumber) {
    return (
      <TelemetryProvider claimNum={claimNumber} baseUrl={baseUrl} tokenProvider={tokenProvider}>
        {children}
      </TelemetryProvider>
    );
  }

  // No claimNumber → safe no-op provider (prevents errors if useTelemetry() is called)
  return <NoopTelemetryProvider>{children}</NoopTelemetryProvider>;
}
